import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import {
  getContactTypeOptions,
  getLanguageOptions,
  getTitleOptions,
} from '@/constants/contactPartnerOptions';
import { useContactPartnerForm } from '@/hooks/useContactPartnerForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { ContactPartnerDialogProps } from '@/types';
import { Save, X } from 'lucide-react';
import { ContactPartnerFormFields } from './ContactPartnerFormFields';

/**
 * Contact Partner Form Dialog Component
 * Handles contact partner creation and editing in a modal dialog
 *
 * Refactored following best practices:
 * - Single Responsibility: UI concerns only, form logic extracted to custom hook
 * - DRY: Reusable form options moved to constants
 * - Declarative: Uses React Hook Form with proper validation
 * - KISS: Simple, clean component structure
 */
export default function ContactPartnerDialog({
  open,
  onOpenChange,
  contactPartner,
  onSave,
  isLoading = false,
}: ContactPartnerDialogProps) {
  const { t: tForms } = useFormsTranslation();

  // Custom hook handles all form logic (Single Responsibility)
  const { form, handleSubmit, isEditing } = useContactPartnerForm({
    contactPartner,
    onSave,
    isOpen: open,
  });

  // Reusable form options (DRY principle)
  const contactTypeOptions = getContactTypeOptions(tForms);
  const languageOptions = getLanguageOptions(tForms);
  const titleOptions = getTitleOptions(tForms);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-3xl max-w-4xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? tForms('contactPartner.dialogTitle.edit')
              : tForms('contactPartner.dialogTitle.add')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? tForms('contactPartner.dialogDescription.edit')
              : tForms('contactPartner.dialogDescription.add')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-6'
          >
            <ContactPartnerFormFields
              form={form}
              tForms={tForms}
              contactTypeOptions={contactTypeOptions}
              languageOptions={languageOptions}
              titleOptions={titleOptions}
            />

            <DialogFooter className='flex gap-2 pt-6'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className='flex items-center gap-2'
              >
                <X className='h-4 w-4' />
                {tForms('contactPartner.buttons.cancel')}
              </Button>
              <Button
                type='submit'
                disabled={isLoading}
                className='flex items-center gap-2'
              >
                <Save className='h-4 w-4' />
                {isLoading
                  ? tForms('contactPartner.buttons.saving')
                  : isEditing
                    ? tForms('contactPartner.buttons.update')
                    : tForms('contactPartner.buttons.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
